import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { PageBanner } from "../components/PageBanner";
import { Reveal } from "../components/Reveal";
import { CheckCircle2, CalendarCheck, Phone, HeartPulse, HandHeart } from "lucide-react";
import { CONTACT } from "../constants/data";

const RATE_CARDS = [
  {
    icon: HeartPulse,
    name: "Care Management",
    description: "Our dedicated care team provides reliable support 24 hours a day, ensuring individuals and families receive compassionate assistance, responsive service, and peace of mind whenever care is needed.",
    features: [
      "Virtual Care Support Platform",
      "In-Home Care Reassessments",
      "Dedicated Care Manager",
      "Supervised Caregiver Training Visits",
      "Personalized Care Plan Development & Coordination",
      "24/7/365 Emergency On-Call Support",
      "Scheduling & Care Coordination",
      "Real-Time Caregiver Monitoring",
      "Virtual Family Room Access",
      "Care Updates & Progress Reports",
      "Coordination with Interdisciplinary Medical Teams",
    ],
    testId: "rate-card-care-management",
  },
  {
    icon: HandHeart,
    name: "Caregiving Services",
    minimum: "Minimum service period of 3 hours",
    description: "You will be thoughtfully matched with a qualified care team possessing the appropriate skills, training, and certifications to meet your individual care needs.",
    features: [
      "Companionship & Social Support",
      "Dementia & Alzheimer's Support",
      "Grocery & Meal Assistance",
      "Homemaking Services",
      "Personal Care Assistance",
      "Medication Reminders",
      "Transportation & Accompaniment",
      "Overnight Care",
      "12-Hour Visits",
    ],
    testId: "rate-card-caregiving-services",
  },
];

export default function RatesPage() {
  return (
    <div data-testid="rates-page">
      <Header />
      <main>
        <PageBanner
          testId="rates-banner"
          eyebrow="Transparent Pricing"
          title="Services & Rates"
          description="Clear, straightforward rates for compassionate, professional home care. Every plan is personalized to your needs — here's what our care includes."
        />

        <section className="py-24 lg:py-32 bg-brand-paper">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {RATE_CARDS.map((card, i) => (
                <Reveal key={card.name} delay={i * 0.12}>
                  <article
                    data-testid={card.testId}
                    className="h-full flex flex-col rounded-2xl border border-slate-200 bg-white p-8 md:p-10 shadow-[0_8px_30px_rgb(11,27,61,0.05)] hover:shadow-[0_20px_50px_rgb(0,128,128,0.12)] hover:border-brand-teal/40 transition-shadow duration-300"
                  >
                    <div className="flex items-center gap-4">
                      <span className="inline-flex h-13 w-13 p-3 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                        <card.icon className="h-6 w-6" strokeWidth={1.5} />
                      </span>
                      <h2 className="font-display text-2xl md:text-3xl text-brand-navy">{card.name}</h2>
                    </div>

                    <div className="mt-6">
                      <Link
                        to="/contact"
                        data-testid={`lets-talk-${card.testId}`}
                        className="inline-block font-display text-3xl md:text-4xl font-semibold text-brand-teal cursor-pointer hover:text-brand-navy hover:underline underline-offset-8 decoration-2 transition-colors duration-300"
                      >
                        Let's Talk
                      </Link>
                      <p className="mt-1 text-[15px] font-semibold text-slate-500">Personalized to your care needs — contact us for your rate</p>
                    </div>
                    {card.minimum && (
                      <p className="mt-2 inline-flex w-fit rounded-full bg-brand-mist px-4 py-1.5 text-sm font-bold text-brand-navy">{card.minimum}</p>
                    )}

                    <p className="mt-5 text-base leading-relaxed text-slate-600">{card.description}</p>

                    <div className="mt-6 pt-6 border-t border-slate-100 flex-1">
                      <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-teal">What's Included</p>
                      <ul className="mt-4 space-y-3">
                        {card.features.map((f) => (
                          <li key={f} className="flex items-start gap-3">
                            <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0 text-brand-teal" strokeWidth={1.8} />
                            <span className="text-[15px] leading-relaxed text-slate-700">{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section data-testid="rates-cta" className="pb-24 lg:pb-32 bg-brand-paper">
          <div className="max-w-6xl mx-auto px-5 lg:px-8">
            <Reveal>
              <div className="rounded-[2rem] bg-brand-navy px-8 py-14 md:p-16 flex flex-col lg:flex-row lg:items-center justify-between gap-10 shadow-[0_24px_60px_rgb(11,27,61,0.18)]">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.3em] text-brand-cyan">Ready to Get Started?</p>
                  <h2 className="mt-4 font-display text-3xl md:text-4xl text-white tracking-tight">Let's build your personalized care plan</h2>
                  <p className="mt-4 max-w-xl text-lg leading-relaxed text-slate-300">
                    Contact BMS Health Care Services Ltd. today to discuss your care needs and receive a personalized service plan and rate.
                  </p>
                </div>
                <div className="flex flex-wrap gap-4 shrink-0">
                  <Link
                    to="/contact"
                    data-testid="rates-consultation-cta"
                    className="inline-flex items-center gap-2.5 whitespace-nowrap rounded-full bg-brand-teal px-7 py-4 text-base md:text-lg font-bold text-white hover:bg-brand-cyan hover:text-brand-navy transition-colors duration-300 active:scale-95"
                  >
                    <CalendarCheck className="h-5 w-5" strokeWidth={1.8} />
                    Book a Free Care Consultation
                  </Link>
                  <a
                    href={CONTACT.phoneHref}
                    data-testid="rates-call-cta"
                    className="inline-flex items-center gap-2.5 whitespace-nowrap rounded-full border border-white/40 px-7 py-4 text-base md:text-lg font-bold text-white hover:bg-white hover:text-brand-navy transition-colors duration-300 active:scale-95"
                  >
                    <Phone className="h-5 w-5" strokeWidth={1.8} />
                    {CONTACT.phone}
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
